import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TeachersService } from '@/shared/api/rest/services/teachers.service'
import { TEACHER_REGISTRY_KEY } from '@/shared/api/hooks/useTeacherRegistry'
import { useAlertService } from '@/shared/lib/useAlertService'

/**
 * Approve / reject / suspend a teacher. All three invalidate the shared
 * registry cache so the list, the detail pane and the notification bell
 * refresh together.
 */
export const useTeacherActions = () => {
    const alertService = useAlertService()
    const queryClient = useQueryClient()

    const invalidate = () =>
        queryClient.invalidateQueries({ queryKey: TEACHER_REGISTRY_KEY })

    const onError = (fallback: string) => (error: Error) =>
        alertService.error(error.message || fallback)

    const approve = useMutation({
        mutationFn: (id: string) => TeachersService.approve(id),
        onSuccess: () => {
            alertService.success('Teacher approved')
            invalidate()
        },
        onError: onError('Failed to approve teacher')
    })

    const reject = useMutation({
        mutationFn: (id: string) => TeachersService.reject(id),
        onSuccess: () => {
            alertService.success('Teacher request rejected')
            invalidate()
        },
        onError: onError('Failed to reject teacher')
    })

    const suspend = useMutation({
        mutationFn: (id: string) => TeachersService.suspend(id),
        onSuccess: () => {
            alertService.success('Teacher access revoked')
            invalidate()
        },
        onError: onError('Failed to revoke access')
    })

    return {
        approve: approve.mutateAsync,
        reject: reject.mutateAsync,
        suspend: suspend.mutateAsync,
        pending: approve.isPending || reject.isPending || suspend.isPending
    }
}
